// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  SecurityUserConfiguration,
  SecurityUserConfigurationsListOptionalParams,
  SecurityUserConfigurationsGetOptionalParams,
  SecurityUserConfigurationsGetResponse,
  SecurityUserConfigurationsCreateOrUpdateOptionalParams,
  SecurityUserConfigurationsCreateOrUpdateResponse,
  SecurityUserConfigurationsDeleteOptionalParams,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a SecurityUserConfigurations. */
export interface SecurityUserConfigurations {
  /**
   * Lists all the network manager security user configurations in a network manager, in a paginated
   * format.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param networkManagerName The name of the network manager.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    networkManagerName: string,
    options?: SecurityUserConfigurationsListOptionalParams,
  ): PagedAsyncIterableIterator<SecurityUserConfiguration>;
  /**
   * Retrieves a network manager security user configuration.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param networkManagerName The name of the network manager.
   * @param configurationName The name of the network manager Security Configuration.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkManagerName: string,
    configurationName: string,
    options?: SecurityUserConfigurationsGetOptionalParams,
  ): Promise<SecurityUserConfigurationsGetResponse>;
  /**
   * Creates or updates a network manager security user configuration.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param networkManagerName The name of the network manager.
   * @param configurationName The name of the network manager Security Configuration.
   * @param securityUserConfiguration The security user configuration to create or update
   * @param options The options parameters.
   */
  createOrUpdate(
    resourceGroupName: string,
    networkManagerName: string,
    configurationName: string,
    securityUserConfiguration: SecurityUserConfiguration,
    options?: SecurityUserConfigurationsCreateOrUpdateOptionalParams,
  ): Promise<SecurityUserConfigurationsCreateOrUpdateResponse>;
  /**
   * Deletes a network manager security user configuration.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param networkManagerName The name of the network manager.
   * @param configurationName The name of the network manager Security Configuration.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    networkManagerName: string,
    configurationName: string,
    options?: SecurityUserConfigurationsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes a network manager security user configuration.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param networkManagerName The name of the network manager.
   * @param configurationName The name of the network manager Security Configuration.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    networkManagerName: string,
    configurationName: string,
    options?: SecurityUserConfigurationsDeleteOptionalParams,
  ): Promise<void>;
}
